import React, { useState, useEffect, useContext } from 'react';

import './styles.scss';

import DataListsProxy from 'api/dataLists/dataListsProxy';

import PageLayout from 'global_elements/Layouts/PageLayout';
import FlexContainer from 'global_elements/Layouts/FlexContainer';
import Paragraph from 'global_elements/Text/Paragraph';
import LabeledSingleSelect from 'global_elements/Inputs/Dropdown/SingleSelect/LabeledSingleSelect';
import InlineText from 'global_elements/Text/InlineText';

import { PageLayoutVariant } from 'global_elements/Layouts/PageLayout/variants';
import { FontColors, FontSizes } from 'global_elements/Text/variants';
import { AlignVariant, DisplayVariant, JustifyVariant } from 'global_elements/Layouts/FlexContainer/variants';
import { SingleStandardDropdownStyles } from 'global_elements/Inputs/Dropdown/SingleSelect/styles';
import { FacilitySelectOption } from 'types/inputProps';

import { UserContext } from 'context/user';
import { ActiveFacilityContext } from 'context/activeFacility';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition, faFolder, faFolderOpen, faFolderPlus, faPencil, faTrash, faSortNumericDesc, faSortNumericAsc, faSortAlphaAsc, faSortAlphaDesc } from '@fortawesome/free-solid-svg-icons';
import { faFile, faFileExcel, faFilePdf, faFileVideo, faFileWord } from '@fortawesome/free-regular-svg-icons';
import AttachmentsProxy from '../../../api/attachments/AttachmentsProxy';
import { AttachmentFolderContentsModel, AttachmentFolderModel, AttachmentModel } from '../../../interfaces/attachments/AttachmentFolderContentsModel';
import TertiaryHeader from '../../../global_elements/Text/TertiaryHeader';
import { Roles } from '../../../constants/roles';
import Button from '../../../global_elements/Button';
import { ButtonVariant } from '../../../global_elements/Button/variants';
import SingleSelect from '../../../global_elements/Inputs/Dropdown/SingleSelect';

type FolderWrapper = {
  folder: AttachmentFolderModel;
  subfolders: FolderWrapper[];
};

const FacilityLibraryPage = (): React.JSX.Element => {
  const { user } = useContext(UserContext);
  const isAdmin = user?.role === Roles.ADMIN || user?.role === Roles.SUPER_ADMIN;
  const [availableFacilities, setAvailableFacilities] = useState<FacilitySelectOption[]>([]);
  const { facility } = useContext(ActiveFacilityContext);
  const [selectedFacility, setSelectedFacility] = useState<FacilitySelectOption>();
  const [facilityFolders, setFacilityFolders] = useState<FolderWrapper[]>();
  const [currentFolder, setCurrentFolder] = useState<FolderWrapper>();
  const [currentFolderContents, setCurrentFolderContents] = useState<AttachmentFolderContentsModel>();
  const [dropzoneHoverClass, setDropzoneHoverClass] = useState<string>('');
  const [dropTarget, setDropTarget] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>();
  const sorts = ['CreatedDateTime desc', 'CreatedDateTime asc', 'Name asc', 'Name desc'];
  const sortIcons = [faSortNumericDesc, faSortNumericAsc, faSortAlphaAsc, faSortAlphaDesc];
  const sortDisplayNames = ['Created', 'Created', 'Name', 'Name'];
  const [currentSort, setCurrentSort] = useState<number>(0);

  const getCurrentSort = (): string => sorts[currentSort];

  useEffect(() => {
    DataListsProxy.getAllUserFacilities(
      (response) => {
        if (response?.data) {
          const facilityOptions: FacilitySelectOption[] = [];

          response.data
            .sort((a, b) => {
              if (a.facilityName.toLowerCase() < b.facilityName.toLowerCase()) {
                return -1;
              }

              if (a.facilityName.toLowerCase() > b.facilityName.toLowerCase()) {
                return 1;
              }

              return 0;
            })
            .forEach((element) => {
              if (element.facilityName && element.facilityID) {
                facilityOptions.push({
                  label: element.facilityName,
                  value: element.facilityID.toString(),
                  isCoreMeasures: !!element.coreMeasures, // Convert numbers to boolean
                  hasServiceCodes: !!element.svcCode,
                  isOutcomes: !!element.outcomes,
                  isUHS: Boolean(element.isUHS && element.isUHS > 0),
                });
              }
            });

          setAvailableFacilities(facilityOptions);
        }
      },
      (errorResponse) => {
        console.log(errorResponse);
      },
    );
  }, []);

  const handleFacilitySelectChange = (option: FacilitySelectOption): void => {
    setSelectedFacility(option);
    setFacilityFolders(undefined);
    setCurrentFolder(undefined);
    setCurrentFolderContents(undefined);
    const facilityId = parseInt(option.value, 10);
    if (Number.isNaN(facilityId) || facilityId === 0) {
      return;
    }
    AttachmentsProxy.getFacilityContents(facilityId).then((r) => {
      if (r.data) {
        const facilityFeatureFolders = r.data.folders.filter((f) => {
          if (option.isOutcomes && f.facilityFolderType === 0) {
            return true;
          }
          if (option.isCoreMeasures && f.facilityFolderType === 1) {
            return true;
          }
          return false;
        });
        const wrappers = facilityFeatureFolders.map<FolderWrapper>((f) => ({ folder: f, subfolders: [] }));
        setFacilityFolders(wrappers);
      }
    });
  };

  useEffect(() => {
    handleFacilitySelectChange(facility);
  }, [facility]);

  const LoadFolder = (folder: FolderWrapper): void => {
    AttachmentsProxy.getFolderContents(folder.folder.attachmentFolderId, getCurrentSort())
      .then((c) => {
        if (c.data) {
          const newSubfolders = c.data.folders.map<FolderWrapper>((f) => ({ folder: f, subfolders: [] }));
          for (let i = 0; i < newSubfolders.length; i += 1) {
            const oldSubfolders = folder.subfolders.filter((sf) => sf.folder.attachmentFolderId === newSubfolders[i].folder.attachmentFolderId);
            if (oldSubfolders.length === 1) {
              newSubfolders[i].subfolders = oldSubfolders[0].subfolders;
            }
          }
          folder.subfolders = newSubfolders;
          setCurrentFolder(folder);
          setCurrentFolderContents(c.data);
        }
      })
      .catch((e) => console.log(e));
  };

  useEffect(() => {
    if (facilityFolders && facilityFolders.length > 0) {
      LoadFolder(facilityFolders[0]);
    }
  }, [facilityFolders]);

  useEffect(() => {
    if (currentFolder) {
      LoadFolder(currentFolder);
    }
  }, [currentSort]);

  const NewFolder = (): void => {
    // eslint-disable-next-line no-alert
    const name = window.prompt('New folder name?', 'New folder');
    if (!name || !currentFolder) {
      return;
    }
    AttachmentsProxy.saveFolder(undefined, currentFolder.folder.attachmentFolderId, name).then(() => {
      LoadFolder(currentFolder);
    });
  };

  const EditFolder = (folder: AttachmentFolderModel): void => {
    // eslint-disable-next-line no-alert
    const newName = window.prompt('New name?', folder.name);
    if (!newName || !currentFolder) {
      return;
    }
    AttachmentsProxy.saveFolder(folder.attachmentFolderId, currentFolder.folder.attachmentFolderId, newName).then(() => {
      LoadFolder(currentFolder);
    });
  };

  const DeleteFolder = (folder: AttachmentFolderModel): void => {
    // eslint-disable-next-line no-alert
    if (!currentFolder || !window.confirm(`Are you sure you want to delete, '${folder.name}'?`)) {
      return;
    }
    AttachmentsProxy.deleteFolder(folder.attachmentFolderId).then(() => {
      LoadFolder(currentFolder);
    });
  };

  const GetFileIcon = (contentType: string): IconDefinition => {
    const lowerContentType = contentType.toLowerCase();
    switch (lowerContentType) {
      case 'application/msword':
        return faFileWord;
      case 'application/pdf':
        return faFilePdf;
      case 'application/vnd.ms-excel':
        return faFileExcel;
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return faFileWord;
      case 'video/mp4':
        return faFileVideo;
      default:
        return faFile;
    }
  };

  const OpenAttachment = (file: AttachmentModel): void => {
    AttachmentsProxy.download(file.attachmentId)
      .then((response) => {
        if (response.data) {
          const dl = new File([response.data], file.name, { type: file.contentType });
          const dlUrl = URL.createObjectURL(dl);
          const a = document.createElement('a');
          document.body.appendChild(a);
          a.href = dlUrl;
          if (file.contentType === 'application/pdf') {
            a.target = '_blank';
          } else {
            a.target = '_self';
            a.download = file.name;
          }
          a.click();
          URL.revokeObjectURL(dlUrl);
        }
      })
      .catch((e) => console.log(e));
  };

  const EditAttachment = (file: AttachmentModel): void => {
    // eslint-disable-next-line no-alert
    const newName = window.prompt('New name?', file.name);
    if (!newName) {
      return;
    }
    AttachmentsProxy.saveAttachment(file.attachmentId, newName).then(() => {
      if (currentFolder) {
        LoadFolder(currentFolder);
      }
    });
  };

  const DeleteAttachment = (file: AttachmentModel): void => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Are you sure you want to delete, '${file.name}'?`)) {
      return;
    }
    AttachmentsProxy.deleteAttachment(file.attachmentId).then(() => {
      if (currentFolder) {
        LoadFolder(currentFolder);
      }
    });
  };

  const UploadFiles = (files: FileList | null): void => {
    if (!files || files.length < 1 || !currentFolder) {
      return;
    }
    AttachmentsProxy.upload(currentFolder.folder.attachmentFolderId, files).then(() => {
      LoadFolder(currentFolder);
    });
  };

  const DragFile = (e: React.DragEvent<HTMLAnchorElement>, file: AttachmentModel): void => {
    if (isAdmin) {
      e.dataTransfer.setData('attachmentId', file.attachmentId);
    }
  };

  const DragFolder = (e: React.DragEvent<HTMLAnchorElement>, folder: AttachmentFolderModel): void => {
    if (isAdmin) {
      e.dataTransfer.setData('attachmentFolderId', folder.attachmentFolderId);
    }
  };

  const AllowDrop = (e: React.DragEvent<HTMLAnchorElement>): void => {
    if (isAdmin) {
      e.preventDefault();
    }
  };

  const FindFolder = (folders: FolderWrapper[], folderId: string): FolderWrapper | undefined => {
    let match = folders.find((ff) => ff.folder.attachmentFolderId === folderId);
    if (match) {
      return match;
    }
    for (let i = 0; i < folders.length; i += 1) {
      const children = folders[i].subfolders;
      if (children) {
        match = FindFolder(children, folderId);
        if (match) {
          return match;
        }
      }
    }
    return undefined;
  };

  const DropOnFolder = (e: React.DragEvent<HTMLAnchorElement>, folder: FolderWrapper): void => {
    if (isAdmin) {
      e.preventDefault();
      const attachmentId = e.dataTransfer.getData('attachmentId');
      if (attachmentId) {
        AttachmentsProxy.moveAttachment(attachmentId, folder.folder.attachmentFolderId).then(() => {
          if (currentFolder) {
            LoadFolder(currentFolder);
          }
        });
        return;
      }
      const folderId = e.dataTransfer.getData('attachmentFolderId');
      if (folderId) {
        AttachmentsProxy.moveFolder(folderId, folder.folder.attachmentFolderId)
          .then(async (r) => {
            if (!r.success) {
              setErrorMessage(r.message);
              setTimeout(() => setErrorMessage(undefined), 5000);
              return;
            }
            if (facilityFolders) {
              const destFolder = FindFolder(facilityFolders, folder.folder.attachmentFolderId);
              if (destFolder) {
                const destContents = await AttachmentsProxy.getFolderContents(folder.folder.attachmentFolderId, getCurrentSort());
                if (destContents.data) {
                  const newSubfolders = destContents.data.folders.map<FolderWrapper>((f) => ({ folder: f, subfolders: [] }));
                  for (let i = 0; i < newSubfolders.length; i += 1) {
                    const oldSubfolders = folder.subfolders.filter((sf) => sf.folder.attachmentFolderId === newSubfolders[i].folder.attachmentFolderId);
                    if (oldSubfolders.length === 1) {
                      newSubfolders[i].subfolders = oldSubfolders[0].subfolders;
                    }
                  }
                  destFolder.subfolders = newSubfolders;
                }
              }
            }
            if (currentFolder) {
              const sourceContents = await AttachmentsProxy.getFolderContents(currentFolder.folder.attachmentFolderId, getCurrentSort());
              if (sourceContents.data) {
                const newSubfolders = sourceContents.data.folders.map<FolderWrapper>((f) => ({ folder: f, subfolders: [] }));
                for (let i = 0; i < newSubfolders.length; i += 1) {
                  const oldSubfolders = folder.subfolders.filter((sf) => sf.folder.attachmentFolderId === newSubfolders[i].folder.attachmentFolderId);
                  if (oldSubfolders.length === 1) {
                    newSubfolders[i].subfolders = oldSubfolders[0].subfolders;
                  }
                }
                currentFolder.subfolders = newSubfolders;
                if (facilityFolders) {
                  const treeFolder = FindFolder(facilityFolders, currentFolder.folder.attachmentFolderId);
                  if (treeFolder) {
                    treeFolder.subfolders = newSubfolders;
                  }
                }
                setCurrentFolder(currentFolder);
                setCurrentFolderContents(sourceContents.data);
              }
            }
          })
          .catch((ex) => console.log(ex));
      }
    }
  };

  const RenderFolder = (folder: FolderWrapper, level: number): React.JSX.Element => (
    <>
      <div className={isAdmin && dropTarget === folder.folder.attachmentFolderId ? ' facility-library-folder-is-drag-target' : ''} style={{ width: '100%' }}>
        <FlexContainer
          display={DisplayVariant.FLEX_ROW}
          align={AlignVariant.START}
          justify={JustifyVariant.START}
          extraClasses={`facility-library-folder ${currentFolder?.folder.attachmentFolderId === folder.folder.attachmentFolderId ? 'facility-library-folder-selected' : ''}`}
        >
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a
            href="#"
            style={{ paddingLeft: `${level * 15}px` }}
            draggable={false}
            onDragEnter={() => setDropTarget(folder.folder.attachmentFolderId)}
            onDragOver={(e) => {
              AllowDrop(e);
              setDropTarget(folder.folder.attachmentFolderId);
            }}
            onDrop={(e) => {
              DropOnFolder(e, folder);
              setDropTarget('');
            }}
            onDragLeave={() => {
              setDropTarget('');
            }}
            onClick={(e) => {
              e.preventDefault();
              LoadFolder(folder);
            }}
          >
            <FontAwesomeIcon icon={currentFolder?.folder.attachmentFolderId === folder.folder.attachmentFolderId ? faFolderOpen : faFolder} style={{ paddingRight: '5px' }} />
            <span>{folder.folder.name}</span>
          </a>
        </FlexContainer>
      </div>
      {folder.subfolders.length > 0 && folder.subfolders.map((sf) => RenderFolder(sf, level + 1))}
    </>
  );

  return (
    <PageLayout layout={PageLayoutVariant.PADDED} testText="Facility Library" pageTitle="Facility Library">
      <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="facility-library-content">
        <form>
          {isAdmin && availableFacilities.length === 0 && (
            <LabeledSingleSelect
              styles={SingleStandardDropdownStyles}
              label="Facility"
              options={[{ label: 'Loading...', value: '' }]}
              defaultValue={{ label: 'Loading...', value: '' }}
              value={{ label: 'Loading...', value: '' }}
              onSelection={handleFacilitySelectChange}
            />
          )}
          {isAdmin && availableFacilities.length > 0 && (
            <LabeledSingleSelect
              styles={SingleStandardDropdownStyles}
              label="Facility"
              options={availableFacilities}
              defaultValue={selectedFacility ?? { label: 'Select a Facility', value: '' }}
              value={selectedFacility}
              onSelection={handleFacilitySelectChange}
            />
          )}
          {!isAdmin && (facility.value === '' || facility.value === '0') && (
            <Paragraph text="Please select a facility in the header to search" fontColor={FontColors.HIGH_PRIORITY} fontSize={FontSizes.EXTRA_LARGE} />
          )}
        </form>
        {selectedFacility && selectedFacility.value !== '0' && facilityFolders && (
          <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="facility-library-container">
            <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} wrap extraClasses="facility-library-folders-container">
              {facilityFolders.map((f) => RenderFolder(f, 0))}
            </FlexContainer>
            {currentFolder && currentFolderContents && (
              <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="facility-library-contents">
                <TertiaryHeader text={currentFolder.folder.name} fontColor={FontColors.PRIMARY} />
                <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="facility-library-admin">
                  {isAdmin && (
                    <Button
                      variant={ButtonVariant.PRIMARY}
                      onClick={() => {
                        NewFolder();
                      }}
                    >
                      <FontAwesomeIcon icon={faFolderPlus} />
                      &nbsp;
                      <InlineText text="New Folder" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
                    </Button>
                  )}
                  <SingleSelect
                    isSearchable={false}
                    styles={SingleStandardDropdownStyles}
                    options={sortDisplayNames.map((s, idx) => ({ label: s, value: idx.toString() }))}
                    defaultValue={{ label: sortDisplayNames[0], value: (0).toString() }}
                    value={{ label: sortDisplayNames[currentSort], value: currentSort.toString() }}
                    onSelection={(o) => setCurrentSort(parseInt(o.value, 10))}
                    getOptionLabel={(o) => (
                        <>
                          <FontAwesomeIcon icon={sortIcons[parseInt(o.value, 10)]} />
                          &nbsp;
                          <span>{o.label}</span>
                        </>
                      ) as unknown as string}
                  />
                </FlexContainer>
                {errorMessage && <InlineText text={errorMessage} fontColor={FontColors.HIGH_PRIORITY} fontSize={FontSizes.REGULAR} extraClasses="facility-library-error" />}
                <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} wrap extraClasses="facility-library-contents-container">
                  {currentFolderContents.folders.map((f) => (
                    <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="facility-library-contents-folder">
                      <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="facility-library-contents-folder-name">
                        <span>
                          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                          <a
                            href="#"
                            draggable={isAdmin}
                            onDragStart={(e) => DragFolder(e, f)}
                            onClick={(e) => {
                              e.preventDefault();
                              LoadFolder(currentFolder.subfolders.filter((sf) => sf.folder.attachmentFolderId === f.attachmentFolderId)[0]);
                            }}
                          >
                            <FontAwesomeIcon icon={faFolder} style={{ paddingRight: '5px' }} />
                            <span>{f.name}</span>
                          </a>
                          {isAdmin && (
                            <span style={{ whiteSpace: 'nowrap' }}>
                              <FontAwesomeIcon
                                icon={faPencil}
                                style={{ paddingLeft: '5px', cursor: 'pointer' }}
                                onClick={() => {
                                  EditFolder(f);
                                }}
                              />
                              <FontAwesomeIcon
                                icon={faTrash}
                                style={{ paddingLeft: '3px', cursor: 'pointer' }}
                                onClick={() => {
                                  DeleteFolder(f);
                                }}
                              />
                            </span>
                          )}
                        </span>
                      </FlexContainer>
                    </FlexContainer>
                  ))}
                  {currentFolderContents.files.map((f) => (
                    <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="facility-library-contents-file">
                      <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="facility-library-contents-file-name">
                        <span>
                          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                          <a
                            href="#"
                            draggable={isAdmin}
                            onDragStart={(e) => DragFile(e, f)}
                            onClick={(e) => {
                              e.preventDefault();
                              OpenAttachment(f);
                            }}
                          >
                            <FontAwesomeIcon icon={GetFileIcon(f.contentType)} style={{ paddingRight: '5px' }} />
                            <span>{f.name}</span>
                          </a>
                          {isAdmin && (
                            <span style={{ whiteSpace: 'nowrap' }}>
                              <FontAwesomeIcon
                                icon={faPencil}
                                style={{ paddingLeft: '5px', cursor: 'pointer' }}
                                onClick={() => {
                                  EditAttachment(f);
                                }}
                              />
                              <FontAwesomeIcon
                                icon={faTrash}
                                style={{ paddingLeft: '3px', cursor: 'pointer' }}
                                onClick={() => {
                                  DeleteAttachment(f);
                                }}
                              />
                            </span>
                          )}
                        </span>
                      </FlexContainer>
                    </FlexContainer>
                  ))}
                </FlexContainer>
                {isAdmin && (
                  <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses={`facility-library-dropzone ${dropzoneHoverClass}`}>
                    <span>Drop Files Here (or click)</span>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => {
                        UploadFiles(e.target.files);
                        e.currentTarget.value = '';
                        setDropzoneHoverClass('');
                      }}
                      onDragEnter={() => setDropzoneHoverClass('facility-library-dropzone-hover')}
                      onDragOver={() => setDropzoneHoverClass('facility-library-dropzone-hover')}
                      onDragLeave={() => setDropzoneHoverClass('')}
                    />
                  </FlexContainer>
                )}
              </FlexContainer>
            )}
          </FlexContainer>
        )}
      </FlexContainer>
    </PageLayout>
  );
};

export default FacilityLibraryPage;
